// Copyright (c) 2024 by Audere

import { LabelerData, LabelingStatus, QCData } from "../api/labelApiSlice";

// Get labeling status for user matching input email
export function getUserLabelStatus(
  annotators: (LabelerData | QCData)[],
  userEmail: string
) {
  const user = annotators.find((annotator) => annotator.email === userEmail);
  if (!user) return undefined;

  return "qc_status" in user ? user.qc_status : user.labeler_status;
}

// Filter annotators by specified status
export function getAnnotatorsStatusGroups(
  annotators: (LabelerData | QCData)[],
  status: LabelingStatus
) {
  return annotators.filter((annotator) => {
    return "labeler_status" in annotator
      ? annotator.labeler_status === status
      : annotator.qc_status === status;
  });
}

// Get logged-in annotator from list
function getCurrentAnnotator(
  annotators: (LabelerData | QCData)[],
  userEmail: string
) {
  return annotators.find((annotator) => annotator.email === userEmail);
}

// Get all annotators except annotator matching the specified email
function getOtherAnnotators(
  annotators: (LabelerData | QCData)[],
  userEmail: string
) {
  return annotators.filter((annotators) => annotators.email !== userEmail);
}

// Order annotators with currentAnnotator first
export function orderAnnotators(
  annotators: (LabelerData | QCData)[],
  userEmail: string
) {
  const currentAnnotator = getCurrentAnnotator(annotators, userEmail);
  const otherAnnotators = getOtherAnnotators(annotators, userEmail);

  return currentAnnotator
    ? [currentAnnotator, ...otherAnnotators]
    : otherAnnotators;
}

// Group annotators by status with logged-in user at the start
export function getStatusGroups(
  annotators: (LabelerData | QCData)[],
  userEmail: string
) {
  return Object.entries(LabelingStatus).map(([, value]) => {
    const filteredAnnotators = getAnnotatorsStatusGroups(annotators, value);
    const orderedAnnotators = orderAnnotators(filteredAnnotators, userEmail);

    return {
      status: value,
      annotators: orderedAnnotators,
    };
  });
}

export function hasSubmittedLabels(
  labelStatus: LabelingStatus | undefined
): boolean {
  return (
    labelStatus === LabelingStatus.label_complete ||
    labelStatus === LabelingStatus.qc_complete
  );
}

export function hasInProgressLabels(labelStatus: LabelingStatus | undefined) {
  return (
    labelStatus === LabelingStatus.label_in_progress ||
    labelStatus === LabelingStatus.qc_in_progress
  );
}
